import './assets/main.css'
import ApiService from './services/api.service'
import * as Sentry from '@sentry/vue'

import { createApp, watch, nextTick } from 'vue'
import { createPinia } from 'pinia'
import { useUserStore } from '@/store/userStore'

import App from './App.vue'
import router from './router'
import 'preline/preline'
import { createI18n } from 'vue-i18n'
import { useLocaleStore } from '@/store/localStore'
import en from '@/i18n/yup/en'
import de from '@/i18n/yup/de'
import { setLocale } from 'yup'

//Import Froala Editor
import 'froala-editor/js/plugins.pkgd.min.js'
//Import third party plugins
import 'froala-editor/js/third_party/font_awesome.min'
import 'froala-editor/js/third_party/image_tui.min'
// Import Froala translation files.
import 'froala-editor/js/languages/de.js'
// Import Froala Editor css files.
import 'froala-editor/css/froala_editor.pkgd.min.css'
import 'froala-editor/css/froala_style.min.css'
import VueFroala from 'vue-froala-wysiwyg'

const app = createApp(App)
const i18n = createI18n({ legacy: false })
const API_URL = import.meta.env.VITE_API_URL
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN
import { OpenAPI } from '@/types/generated/core/OpenAPI'
import { toast } from '@/components/ui/toast'
import posthog from 'posthog-js'
import posthogPlugin from './plugins/posthog'

app.use(createPinia())
app.use(router)

const setupApiV3 = () => {
  /**
   * Set up the API service:
   * - Initialize the API service
   * - Set the API service header
   * - Set up the API service interceptors
   * - Set the API service base URL
   */
  OpenAPI.BASE = new URL(API_URL).origin
  OpenAPI.WITH_CREDENTIALS = true
  OpenAPI.interceptors.request.use(async (request) => {
    const value = `; ${document.cookie}`
    const parts = value.split(`; csrftoken=`)
    const token = parts.pop()?.split(';').shift()
    request.headers.set('X-CSRFTOKEN', token ? token : '')
    console.log('interceptor (request)', request)
    return request
  })
  OpenAPI.interceptors.response.use(async (response) => {
    console.log('interceptor (response)', response)
    if (
      response.status === 400 ||
      response.status === 401 ||
      response.status === 403 ||
      response.status === 404 ||
      response.status === 500
    ) {
      // for 403 status, we don't want to show an error message. For those, we
      // need to re-authenticate the user through the login page.
      if (response.status !== 403) {
        toast({
          title: 'Error',
          description: 'There was an error processing your request. Please try again.',
          variant: 'destructive'
        })
      }
    }
    return response
  })
}
setupApiV3()

const setupApiV2 = () => {
  /**
   * Set up the API service:
   * - Initialize the API service
   * - Set the API service header
   */
  ApiService.init(API_URL)
  ApiService.setHeader()
}
setupApiV2()

const setupSentry = () => {
  /**
   * Set up Sentry:
   * - Initialize Sentry
   * - Set up integrations
   */
  if (SENTRY_DSN !== undefined && SENTRY_DSN !== '') {
    Sentry.init({
      app,
      dsn: import.meta.env.VITE_SENTRY_DSN,
      release: import.meta.env.VITE_SENTRY_RELEASE,
      integrations: [
        Sentry.browserTracingIntegration({ router }),
        Sentry.replayIntegration(),
        posthog.sentryIntegration({
          organization: import.meta.env.SENTRY_ORGANIZATION,
          projectId: import.meta.env.SENTRY_PROJECT_ID,
          severityAllowList: ['error', 'info']
        })
      ],

      tracesSampleRate: 1.0,

      // // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
      // tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],

      // Capture Replay for 10% of all sessions,
      // plus for 100% of sessions with an error
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0
    })
  }
}
// setupSentry()

const setupTranslations = () => {
  /**
   * Set up translations:
   * - Load the default locale
   * - Watch for locale changes
   * - Load the correct translations
   * - Set the locale
   */
  const localeStore = useLocaleStore()

  const loadYupTranslations = (locale: string) => {
    switch (locale) {
      case 'de':
        setLocale(de)
        break
      case 'en':
      default:
        setLocale(en)
        break
    }
  }

  // Initial load
  loadYupTranslations(localeStore.getLocale)

  // Watch for locale changes
  watch(
    () => localeStore.getLocale,
    (newLocale) => {
      loadYupTranslations(newLocale)
    }
  )

  app.use(i18n)
}
setupTranslations()
app.use(VueFroala)

const setupPostHog = () => {
  /**
   * Set up PostHog:
   * - Initialize PostHog
   * - Identify the user
   * - Capture page views
   */

  console.log('API_URL', API_URL)
  app.use(posthogPlugin)
  const userStore = useUserStore()
  const { fetchUser } = userStore
  fetchUser().then((user) => {
    if (user) {
      app.config.globalProperties.$posthog.identify(user.uid, {
        id: user.uid,
        uuid: user.uuid,
        oid: user.org.oid,
        ouuid: user.org.uuid
      })
      app.config.globalProperties.$posthog.group('company', user.org.oid, {
        oid: user.org.oid,
        ouuid: user.org.uuid
      })
    }
  })
  router.afterEach((to, from, failure) => {
    if (!failure) {
      nextTick(() => {
        app.config.globalProperties.$posthog.capture('$pageview', { path: to.fullPath })
      })
    }
  })
}
// setupPostHog()

app.mount('#app')
